import { create, StateCreator } from "zustand";
import {
  createJSONStorage,
  devtools,
  persist,
  StateStorage,
} from "zustand/middleware";
import { get, set, del } from "idb-keyval";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import {
  WorkflowImageI,
  WorkflowTwoActionI,
  WorkflowTwoI,
} from "../../../../../../libs/interfaces";
import { removeImageBlob, saveImageBlob } from "../../../../../../libs/helpers";

// Custom storage object
const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    return (await get(name)) || null;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await set(name, value);
  },
  removeItem: async (name: string): Promise<void> => {
    await del(name);
  },
};

export const createWorkflowSlice: StateCreator<
  WorkflowTwoI & WorkflowTwoActionI,
  [],
  []
> = (set, get) => ({
  id: null,
  container: null,
  coordinates: "",
  client: null,
  clientId: null,
  clientIdentification: null,
  labelSerial: null,
  plateVehicle: "",
  driverName: "",
  driverIdentification: "",
  hourInit: "",
  hourEnd: "",
  startProcess: "",
  endProcess: "",
  address: "",
  city: "",
  typeReview: "",
  storageName: "",
  createdByAdmin: null,
  quantityImages: 8,
  images: [],
  imagesDelete: [],
  companyTransport: "",
  naviera: "",
  typeContainer: "",
  size: null,
  observation: null,
  openedBy: "",
  openedWas: "No",
  //FOTOS VACIO EXTERNO
  emptyPanoramicCheckPhoto: "",
  emptyPanoramicCheckB64Photo: "",
  emptyPanoramicValidationPhoto: "",
  emptyPanoramicCheckStatus: null,
  emptyPanoramicCheckComment: null,
  emptyStampNavieraCheckPhoto: "",
  emptyStampNavieraCheckB64Photo: "",
  emptyStampNavieraValidationPhoto: "",
  emptyStampNavieraCheckComment: null,
  emptyStampNavieraCheckStatus: null,
  emptyOtherStampCheckPhoto: "",
  emptyOtherStampCheckB64Photo: "",
  emptyOtherStampValidationPhoto: "",
  emptyOtherStampCheckComment: null,
  emptyOtherStampCheckStatus: null,
  emptySatelliteLockCheckPhoto: "",
  emptySatelliteLockCheckB64Photo: "",
  emptySatelliteLockValidationPhoto: "",
  emptySatelliteLockCheckComment: null,
  emptySatelliteLockCheckStatus: null,
  exitEngineryCheckPhoto1: "",
  exitEngineryCheckB64Photo1: "",
  exitEngineryValidationPhoto1: "",
  exitEngineryCheckComment1: null,
  exitEngineryCheckStatus1: null,
  exitEngineryCheckPhoto2: "",
  exitEngineryCheckB64Photo2: "",
  exitEngineryValidationPhoto2: "",
  exitEngineryCheckComment2: null,
  exitEngineryCheckStatus2: null,
  exitDoorCheckVideo: "",
  exitDoorCheckB64Video: "",
  exitDoorCheckComment: "",
  exitDoorCheckStatus: null,
  exitDoorValidationVideo: "",
  exitEngineryCheckVideo: "",
  exitEngineryCheckB64Video: "",
  exitEngineryCheckComment: "",
  exitEngineryCheckStatus: null,
  exitEngineryValidationVideo: "",
  //FOTOS CARGA
  chargingProcessPhoto1: "",
  chargingProcessComment1: "",
  chargingProcessPhoto2: "",
  chargingProcessComment2: "",
  chargingProcessVideo: "",
  //FOTOS CONTENEDOR
  containerPanoramicPhoto: "",
  containerPanoramicComment: "",
  containerPanoramicCoordinates: "",
  navieraBottlePhoto: "",
  navieraBottleComment: "",
  navieraWirePhoto: "",
  navieraWireComment: "",
  navieraLabelPhoto: "",
  navieraLabelComment: "",
  exporterBottlePhoto: "",
  exporterBottleComment: "",
  exporterWirePhoto: "",
  exporterWireComment: "",
  exporterLabelPhoto: "",
  exporterLabelComment: "",
  otherBottlePhoto: "",
  otherBottleComment: "",
  otherWirePhoto: "",
  otherWireComment: "",
  otherLabelPhoto: "",
  otherLabelComment: "",
  gpsPhoto: "",
  gpsComment: "",
  gpsStampPhoto: "",
  gpsStampComment: "",
  entryPort: "",
  exitDoorVideo: "",
  exitEngineryVideo: "",

  setCoordinates: (coordinates) => {
    set(() => ({ coordinates }));
  },
  setCompanyTransport: (companyTransport) => set(() => ({ companyTransport })),
  setObservation: (observation) => set(() => ({ observation })),
  setNaviera: (naviera) => set(() => ({ naviera })),
  setTypeContainer: (typeContainer) => set(() => ({ typeContainer })),
  setSize: (size) => set(() => ({ size })),
  setCreatedByAdmin: (createdByAdmin) => set(() => ({ createdByAdmin })),
  setOpenedBy: (openedBy) => {
    set(() => ({ openedBy }));
  },
  setOpenedWas: (openedWas) => {
    set(() => ({ openedWas }));
  },

  setEmptyPanoramicCheckPhoto: (emptyPanoramicCheckPhoto) =>
    set(() => ({ emptyPanoramicCheckPhoto })),
  setEmptyPanoramicCheckB64Photo: async (file) => {
    const getImageBack = get().emptyPanoramicCheckB64Photo;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ emptyPanoramicCheckB64Photo: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyPanoramicCheckB64Photo: photoId }));
  },
  setEmptyPanoramicValidationPhoto: async (file) => {
    const getImageBack = get().emptyPanoramicValidationPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyPanoramicValidationPhoto: photoId }));
  },
  setEmptyPanoramicCheckStatus: (emptyPanoramicCheckStatus) =>
    set(() => ({ emptyPanoramicCheckStatus })),
  setEmptyPanoramicCheckComment: (emptyPanoramicCheckComment) =>
    set(() => ({ emptyPanoramicCheckComment })),

  setEmptyStampNavieraCheckPhoto: (emptyStampNavieraCheckPhoto) =>
    set(() => ({ emptyStampNavieraCheckPhoto })),
  setEmptyStampNavieraCheckB64Photo: async (file) => {
    const getImageBack = get().emptyStampNavieraCheckB64Photo;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ emptyStampNavieraCheckB64Photo: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyStampNavieraCheckB64Photo: photoId }));
  },
  setEmptyStampNavieraValidationPhoto: async (file) => {
    const getImageBack = get().emptyStampNavieraValidationPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyStampNavieraValidationPhoto: photoId }));
  },
  setEmptyStampNavieraCheckComment: (emptyStampNavieraCheckComment) =>
    set(() => ({ emptyStampNavieraCheckComment })),
  setEmptyStampNavieraCheckStatus: (emptyStampNavieraCheckStatus) =>
    set(() => ({ emptyStampNavieraCheckStatus })),

  setEmptyOtherStampCheckPhoto: (emptyOtherStampCheckPhoto) =>
    set(() => ({ emptyOtherStampCheckPhoto })),
  setEmptyOtherStampCheckB64Photo: async (file) => {
    const getImageBack = get().emptyOtherStampCheckB64Photo;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ emptyOtherStampCheckB64Photo: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyOtherStampCheckB64Photo: photoId }));
  },
  setEmptyOtherStampValidationPhoto: async (file) => {
    const getImageBack = get().emptyOtherStampValidationPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptyOtherStampValidationPhoto: photoId }));
  },
  setEmptyOtherStampCheckComment: (emptyOtherStampCheckComment) =>
    set(() => ({ emptyOtherStampCheckComment })),
  setEmptyOtherStampCheckStatus: (emptyOtherStampCheckStatus) =>
    set(() => ({ emptyOtherStampCheckStatus })),

  setEmptySatelliteLockCheckPhoto: (emptySatelliteLockCheckPhoto) =>
    set(() => ({ emptySatelliteLockCheckPhoto })),
  setEmptySatelliteLockCheckB64Photo: async (file) => {
    const getImageBack = get().emptySatelliteLockCheckB64Photo;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ emptySatelliteLockCheckB64Photo: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySatelliteLockCheckB64Photo: photoId }));
  },
  setEmptySatelliteLockValidationPhoto: async (file) => {
    const getImageBack = get().emptySatelliteLockValidationPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ emptySatelliteLockValidationPhoto: photoId }));
  },
  setEmptySatelliteLockCheckComment: (emptySatelliteLockCheckComment) =>
    set(() => ({ emptySatelliteLockCheckComment })),
  setEmptySatelliteLockCheckStatus: (emptySatelliteLockCheckStatus) =>
    set(() => ({ emptySatelliteLockCheckStatus })),

  setExitEngineryCheckPhoto1: (exitEngineryCheckPhoto1) =>
    set(() => ({ exitEngineryCheckPhoto1 })),
  setExitEngineryCheckB64Photo1: async (file) => {
    const getImageBack = get().exitEngineryCheckB64Photo1;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ exitEngineryCheckB64Photo1: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryCheckB64Photo1: photoId }));
  },
  setExitEngineryValidationPhoto1: async (file) => {
    const getImageBack = get().exitEngineryValidationPhoto1;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryValidationPhoto1: photoId }));
  },
  setExitEngineryCheckComment1: (exitEngineryCheckComment1) =>
    set(() => ({ exitEngineryCheckComment1 })),
  setExitEngineryCheckStatus1: (exitEngineryCheckStatus1) =>
    set(() => ({ exitEngineryCheckStatus1 })),

  setExitEngineryCheckPhoto2: (exitEngineryCheckPhoto2) =>
    set(() => ({ exitEngineryCheckPhoto2 })),
  setExitEngineryCheckB64Photo2: async (file) => {
    const getImageBack = get().exitEngineryCheckB64Photo2;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ exitEngineryCheckB64Photo2: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryCheckB64Photo2: photoId }));
  },
  setExitEngineryValidationPhoto2: async (file) => {
    const getImageBack = get().exitEngineryValidationPhoto2;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryValidationPhoto2: photoId }));
  },
  setExitEngineryCheckComment2: (exitEngineryCheckComment2) =>
    set(() => ({ exitEngineryCheckComment2 })),
  setExitEngineryCheckStatus2: (exitEngineryCheckStatus2) =>
    set(() => ({ exitEngineryCheckStatus2 })),

  setExitDoorCheckVideo: (exitDoorCheckVideo) =>
    set(() => ({ exitDoorCheckVideo })),
  setExitDoorCheckB64Video: async (file) => {
    const getImageBack = get().exitDoorCheckB64Video;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ exitDoorCheckB64Video: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitDoorCheckB64Video: photoId }));
  },
  setExitDoorCheckComment: (exitDoorCheckComment) =>
    set(() => ({ exitDoorCheckComment })),
  setExitDoorCheckStatus: (exitDoorCheckStatus) =>
    set(() => ({ exitDoorCheckStatus })),
  setExitDoorValidationVideo: async (file) => {
    if (!file) {
      set(() => ({ exitDoorValidationVideo: "" }));
      return;
    }
    set(() => ({ exitDoorValidationVideo: file as string }));
  },

  setExitEngineryCheckVideo: (exitEngineryCheckVideo) =>
    set(() => ({ exitEngineryCheckVideo })),
  setExitEngineryCheckB64Video: async (file) => {
    const getImageBack = get().exitEngineryCheckB64Video;
    if (getImageBack) await removeImageBlob(getImageBack);

    if (!file) {
      set(() => ({ exitEngineryCheckB64Video: "" }));
      return;
    }

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exitEngineryCheckB64Video: photoId }));
  },
  setExitEngineryCheckComment: (exitEngineryCheckComment) =>
    set(() => ({ exitEngineryCheckComment })),
  setExitEngineryCheckStatus: (exitEngineryCheckStatus) =>
    set(() => ({ exitEngineryCheckStatus })),
  setExitEngineryValidationVideo: async (file) => {
    if (!file) {
      set(() => ({ exitEngineryValidationVideo: "" }));
      return;
    }
    set(() => ({ exitEngineryValidationVideo: file as string }));
  },

  setChargingProcessPhoto1: async (file) => {
    const getImageBack = get().chargingProcessPhoto1;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ chargingProcessPhoto1: photoId }));
  },
  setChargingProcessComment1: (chargingProcessComment1) =>
    set(() => ({ chargingProcessComment1 })),
  setChargingProcessPhoto2: async (file) => {
    const getImageBack = get().chargingProcessPhoto2;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ chargingProcessPhoto2: photoId }));
  },
  setChargingProcessComment2: (chargingProcessComment2) =>
    set(() => ({ chargingProcessComment2 })),
  setChargingProcessVideo: async (file) => {
    if (!file) {
      set(() => ({ chargingProcessVideo: "" }));
      return;
    }
    set(() => ({ chargingProcessVideo: file as string }));
  },

  setContainerPanoramicPhoto: async (file) => {
    const getImageBack = get().containerPanoramicPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    const coordinates = get().coordinates;
    set(() => ({
      containerPanoramicPhoto: photoId,
      containerPanoramicCoordinates: coordinates,
    }));
  },
  setContainerPanoramicComment: (containerPanoramicComment) =>
    set(() => ({ containerPanoramicComment })),
  setContainerPanoramicCoordinates: (containerPanoramicCoordinates) =>
    set(() => ({ containerPanoramicCoordinates })),

  setNavieraBottlePhoto: async (file) => {
    const getImageBack = get().navieraBottlePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ navieraBottlePhoto: photoId }));
  },
  setNavieraBottleComment: (navieraBottleComment) =>
    set(() => ({ navieraBottleComment })),
  setNavieraWirePhoto: async (file) => {
    const getImageBack = get().navieraWirePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ navieraWirePhoto: photoId }));
  },
  setNavieraWireComment: (navieraWireComment) =>
    set(() => ({ navieraWireComment })),
  setNavieraLabelPhoto: async (file) => {
    const getImageBack = get().navieraLabelPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ navieraLabelPhoto: photoId }));
  },
  setNavieraLabelComment: (navieraLabelComment) =>
    set(() => ({ navieraLabelComment })),

  setExporterBottlePhoto: async (file) => {
    const getImageBack = get().exporterBottlePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exporterBottlePhoto: photoId }));
  },
  setExporterBottleComment: (exporterBottleComment) =>
    set(() => ({ exporterBottleComment })),
  setExporterWirePhoto: async (file) => {
    const getImageBack = get().exporterWirePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exporterWirePhoto: photoId }));
  },
  setExporterWireComment: (exporterWireComment) =>
    set(() => ({ exporterWireComment })),
  setExporterLabelPhoto: async (file) => {
    const getImageBack = get().exporterLabelPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ exporterLabelPhoto: photoId }));
  },
  setExporterLabelComment: (exporterLabelComment) =>
    set(() => ({ exporterLabelComment })),

  setOtherBottlePhoto: async (file) => {
    const getImageBack = get().otherBottlePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ otherBottlePhoto: photoId }));
  },
  setOtherBottleComment: (otherBottleComment) =>
    set(() => ({ otherBottleComment })),
  setOtherWirePhoto: async (file) => {
    const getImageBack = get().otherWirePhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ otherWirePhoto: photoId }));
  },
  setOtherWireComment: (otherWireComment) => set(() => ({ otherWireComment })),
  setOtherLabelPhoto: async (file) => {
    const getImageBack = get().otherLabelPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ otherLabelPhoto: photoId }));
  },
  setOtherLabelComment: (otherLabelComment) =>
    set(() => ({ otherLabelComment })),

  setGpsPhoto: async (file) => {
    const getImageBack = get().gpsPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ gpsPhoto: photoId }));
  },
  setGpsComment: (gpsComment) => set(() => ({ gpsComment })),
  setGpsStampPhoto: async (file) => {
    const getImageBack = get().gpsStampPhoto;
    if (getImageBack) await removeImageBlob(getImageBack);

    const photoId = crypto.randomUUID();
    const blob = await (await fetch(file)).blob();
    await saveImageBlob(photoId, blob);

    set(() => ({ gpsStampPhoto: photoId }));
  },
  setGpsStampComment: (gpsStampComment) => set(() => ({ gpsStampComment })),
  setEntryPort: (entryPort) => set(() => ({ entryPort })),
  setExitDoorVideo: async (file) => {
    if (!file) {
      set(() => ({ exitDoorVideo: "" }));
      return;
    }
    set(() => ({ exitDoorVideo: file as string }));
  },
  setExitEngineryVideo: async (file) => {
    if (!file) {
      set(() => ({ exitEngineryVideo: "" }));
      return;
    }
    set(() => ({ exitEngineryVideo: file as string }));
  },

  setId: (id) => set(() => ({ id })),
  setHourInit: (hourInit) => set(() => ({ hourInit })),
  setHourEnd: (hourEnd) => set(() => ({ hourEnd })),
  setStartProcess: (startProcess) => set(() => ({ startProcess })),
  setEndProcess: (endProcess) => set(() => ({ endProcess })),
  setAddress: (address) => set(() => ({ address })),
  setCity: (city) => set(() => ({ city })),
  setTypeReview: (typeReview) => set(() => ({ typeReview })),
  setStorageName: (storageName) => set(() => ({ storageName })),

  setContainer: (container) => {
    if (get().container !== "" || get().container !== null) {
      const now = moment();
      const hourGenerate = now.format("HH:mm:ss");
      set(() => ({ container, hourInit: hourGenerate }));
    } else {
      set(() => ({ container }));
    }
  },
  setClient: (client) => set(() => ({ client })),
  setClientId: (clientId) => set(() => ({ clientId })),
  setClientIdentification: (clientIdentification) =>
    set(() => ({ clientIdentification })),
  setLabelSerial: (labelSerial) => set(() => ({ labelSerial })),
  setPlateVehicle: (plateVehicle) => set(() => ({ plateVehicle })),
  setDriverName: (driverName) => set(() => ({ driverName })),
  setDriverIdentification: (driverIdentification) =>
    set(() => ({ driverIdentification })),
  setImages: (images) => set(() => ({ images })),
  getOneImage: (uuid) => {
    const items_ = get().images;
    return items_.find((el) => el.uuid === uuid)!;
  },
  addImage: (type: any) => {
    set((state) => ({
      images: [
        ...state.images,
        {
          id: null,
          src: "",
          comment: "",
          uuid: uuidv4(),
          model: "wf-two",
          type,
          order: state.images.length + 1,
        },
      ],
    }));
  },
  addImageDelete: (id) => set(() => ({ id: null })),
  removeImage: async (uuid) => {
    const items_ = get().images;
    const itemsDelete_ = get().imagesDelete!;
    const fill = items_.find((el) => el.uuid === uuid);
    const arrnew = items_.filter((el) => el.uuid !== uuid);

    if (fill) await removeImageBlob(fill.src);

    if (fill!.id && !itemsDelete_.includes(fill!.id))
      itemsDelete_.push(fill!.id);

    set(() => ({
      images: arrnew,
      imagesDelete: itemsDelete_,
    }));
  },
  updateImage: async (uuid, field, value) => {
    let laPhoto = "";

    if (uuid && field === "src") {
      await removeImageBlob(uuid);
      const photoId = crypto.randomUUID();

      const blob = await (await fetch(value)).blob();
      await saveImageBlob(photoId, blob);

      laPhoto = photoId;
    }

    set((state) => {
      const items_ = state.images;
      const arrnew: WorkflowImageI[] = [];

      for (const x of items_) {
        if (x.uuid === uuid) {
          const obj = {
            ...x,
            [field]: field === "src" ? laPhoto : value,
          };
          arrnew.push(obj);
        } else {
          arrnew.push(x);
        }
      }
      return {
        images: arrnew,
      };
    });
  },
  getOnlyPhotos: () => ({
    emptyPanoramicCheckB64Photo: get().emptyPanoramicCheckB64Photo!,
    emptyPanoramicValidationPhoto: get().emptyPanoramicValidationPhoto,
    emptyStampNavieraCheckB64Photo: get().emptyStampNavieraCheckB64Photo!,
    emptyStampNavieraValidationPhoto: get().emptyStampNavieraValidationPhoto,
    emptyOtherStampCheckB64Photo: get().emptyOtherStampCheckB64Photo!,
    emptyOtherStampValidationPhoto: get().emptyOtherStampValidationPhoto,
    emptySatelliteLockCheckB64Photo: get().emptySatelliteLockCheckB64Photo!,
    emptySatelliteLockValidationPhoto: get().emptySatelliteLockValidationPhoto,
    exitEngineryCheckB64Photo1: get().exitEngineryCheckB64Photo1!,
    exitEngineryValidationPhoto1: get().exitEngineryValidationPhoto1,
    exitEngineryCheckB64Photo2: get().exitEngineryCheckB64Photo2!,
    exitEngineryValidationPhoto2: get().exitEngineryValidationPhoto2,
    exitDoorCheckB64Video: get().exitDoorCheckB64Video ?? "",
    exitEngineryCheckB64Video: get().exitEngineryCheckB64Video ?? "",
    chargingProcessPhoto1: get().chargingProcessPhoto1,
    chargingProcessPhoto2: get().chargingProcessPhoto2,
    containerPanoramicPhoto: get().containerPanoramicPhoto,
    navieraBottlePhoto: get().navieraBottlePhoto,
    navieraWirePhoto: get().navieraWirePhoto,
    navieraLabelPhoto: get().navieraLabelPhoto,
    exporterBottlePhoto: get().exporterBottlePhoto,
    exporterWirePhoto: get().exporterWirePhoto,
    exporterLabelPhoto: get().exporterLabelPhoto,
    otherBottlePhoto: get().otherBottlePhoto,
    otherWirePhoto: get().otherWirePhoto,
    otherLabelPhoto: get().otherLabelPhoto,
    gpsPhoto: get().gpsPhoto,
    gpsStampPhoto: get().gpsStampPhoto,
  }),
  getValues: () => ({
    container: get().container,
    coordinates: get().coordinates,
    createdByAdmin: get().createdByAdmin,
    observation: get().observation,
    clientId: get().clientId,
    client: get().client,
    labelSerial: get().labelSerial,
    plateVehicle: get().plateVehicle,
    driverName: get().driverName,
    driverIdentification: get().driverIdentification,
    openedBy: get().openedBy,
    openedWas: get().openedWas,
    hourInit: get().hourInit,
    hourEnd: get().hourEnd,
    startProcess: get().startProcess,
    endProcess: get().endProcess,
    address: get().address,
    city: get().city,
    typeReview: get().typeReview,
    storageName: get().storageName,
    images: get().images,
    imagesDelete: get().imagesDelete,
    emptyPanoramicCheckPhoto: get().emptyPanoramicCheckPhoto,
    emptyPanoramicValidationPhoto: get().emptyPanoramicValidationPhoto,
    emptyPanoramicCheckStatus: get().emptyPanoramicCheckStatus,
    emptyPanoramicCheckComment: get().emptyPanoramicCheckComment,
    emptyStampNavieraCheckPhoto: get().emptyStampNavieraCheckPhoto,
    emptyStampNavieraValidationPhoto: get().emptyStampNavieraValidationPhoto,
    emptyStampNavieraCheckComment: get().emptyStampNavieraCheckComment,
    emptyStampNavieraCheckStatus: get().emptyStampNavieraCheckStatus,
    emptyOtherStampCheckPhoto: get().emptyOtherStampCheckPhoto,
    emptyOtherStampValidationPhoto: get().emptyOtherStampValidationPhoto,
    emptyOtherStampCheckComment: get().emptyOtherStampCheckComment,
    emptyOtherStampCheckStatus: get().emptyOtherStampCheckStatus,
    emptySatelliteLockCheckPhoto: get().emptySatelliteLockCheckPhoto,
    emptySatelliteLockValidationPhoto: get().emptySatelliteLockValidationPhoto,
    emptySatelliteLockCheckComment: get().emptySatelliteLockCheckComment,
    emptySatelliteLockCheckStatus: get().emptySatelliteLockCheckStatus,
    exitEngineryCheckPhoto1: get().exitEngineryCheckPhoto1,
    exitEngineryValidationPhoto1: get().exitEngineryValidationPhoto1,
    exitEngineryCheckComment1: get().exitEngineryCheckComment1,
    exitEngineryCheckStatus1: get().exitEngineryCheckStatus1,
    exitEngineryCheckPhoto2: get().exitEngineryCheckPhoto2,
    exitEngineryValidationPhoto2: get().exitEngineryValidationPhoto2,
    exitEngineryCheckComment2: get().exitEngineryCheckComment2,
    exitEngineryCheckStatus2: get().exitEngineryCheckStatus2,
    exitDoorCheckVideo: get().exitDoorCheckVideo,
    exitDoorCheckComment: get().exitDoorCheckComment,
    exitDoorCheckStatus: get().exitDoorCheckStatus,
    exitDoorValidationVideo: get().exitDoorValidationVideo,
    exitEngineryCheckVideo: get().exitEngineryCheckVideo,
    exitEngineryCheckComment: get().exitEngineryCheckComment,
    exitEngineryCheckStatus: get().exitEngineryCheckStatus,
    exitEngineryValidationVideo: get().exitEngineryValidationVideo,
    chargingProcessPhoto1: get().chargingProcessPhoto1,
    chargingProcessComment1: get().chargingProcessComment1,
    chargingProcessPhoto2: get().chargingProcessPhoto2,
    chargingProcessComment2: get().chargingProcessComment2,
    chargingProcessVideo: get().chargingProcessVideo,
    containerPanoramicPhoto: get().containerPanoramicPhoto,
    containerPanoramicComment: get().containerPanoramicComment,
    containerPanoramicCoordinates: get().containerPanoramicCoordinates,
    navieraBottlePhoto: get().navieraBottlePhoto,
    navieraBottleComment: get().navieraBottleComment,
    navieraWirePhoto: get().navieraWirePhoto,
    navieraWireComment: get().navieraWireComment,
    navieraLabelPhoto: get().navieraLabelPhoto,
    navieraLabelComment: get().navieraLabelComment,
    exporterBottlePhoto: get().exporterBottlePhoto,
    exporterBottleComment: get().exporterBottleComment,
    exporterWirePhoto: get().exporterWirePhoto,
    exporterWireComment: get().exporterWireComment,
    exporterLabelPhoto: get().exporterLabelPhoto,
    exporterLabelComment: get().exporterLabelComment,
    otherBottlePhoto: get().otherBottlePhoto,
    otherBottleComment: get().otherBottleComment,
    otherWirePhoto: get().otherWirePhoto,
    otherWireComment: get().otherWireComment,
    otherLabelPhoto: get().otherLabelPhoto,
    otherLabelComment: get().otherLabelComment,
    gpsPhoto: get().gpsPhoto,
    gpsComment: get().gpsComment,
    gpsStampPhoto: get().gpsStampPhoto,
    gpsStampComment: get().gpsStampComment,
    entryPort: get().entryPort,
    exitDoorVideo: get().exitDoorVideo,
    exitEngineryVideo: get().exitEngineryVideo,
    companyTransport: get().companyTransport,
    naviera: get().naviera,
    typeContainer: get().typeContainer,
    size: get().size,
  }),
  setValues: (ladata) => {
    set(ladata);
    set(() => ({ imagesDelete: [] }));
  },
  getImages: () => get().images,
  onClear: async () => {
    const state = get();
    const keys = [
      state.emptyPanoramicCheckB64Photo,
      state.emptyPanoramicValidationPhoto,
      state.emptyStampNavieraCheckB64Photo,
      state.emptyStampNavieraValidationPhoto,
      state.emptyOtherStampCheckB64Photo,
      state.emptyOtherStampValidationPhoto,
      state.emptySatelliteLockCheckB64Photo,
      state.emptySatelliteLockValidationPhoto,
      state.exitEngineryCheckB64Photo1,
      state.exitEngineryValidationPhoto1,
      state.exitEngineryCheckB64Photo2,
      state.exitEngineryValidationPhoto2,
      state.exitDoorCheckB64Video,
      state.exitEngineryCheckB64Video,
      state.chargingProcessPhoto1,
      state.chargingProcessPhoto2,
      state.chargingProcessVideo,
      state.containerPanoramicPhoto,
      state.navieraBottlePhoto,
      state.navieraWirePhoto,
      state.navieraLabelPhoto,
      state.exporterBottlePhoto,
      state.exporterWirePhoto,
      state.exporterLabelPhoto,
      state.otherBottlePhoto,
      state.otherWirePhoto,
      state.otherLabelPhoto,
      state.gpsPhoto,
      state.gpsStampPhoto,
      state.exitDoorVideo,
      state.exitEngineryVideo,
      state.exitEngineryValidationVideo,
      state.exitDoorValidationVideo,
    ];

    for (const key of keys) {
      if (
        key &&
        !key.startsWith("http://") &&
        !key.startsWith("https://") &&
        !key.startsWith("data:")
      ) {
        await removeImageBlob(key);
      }
    }

    const images_ = get().images;
    for (const x of images_) {
      if (
        x.src.startsWith("http://") ||
        x.src.startsWith("https://") ||
        x.src.startsWith("data:")
      )
        continue;
      await removeImageBlob(x.src);
    }

    set(() => ({
      id: null,
      container: null,
      createdByAdmin: null,
      client: null,
      observation: null,
      openedBy: "",
      openedWas: "No",
      labelSerial: null,
      plateVehicle: "",
      driverName: "",
      driverIdentification: "",
      hourInit: "",
      hourEnd: "",
      startProcess: "",
      endProcess: "",
      address: "",
      city: "",
      typeReview: "",
      storageName: "",
      images: [],
      imagesDelete: [],
      step: "Formulario",
      companyTransport: "",
      naviera: "",
      typeContainer: "",
      size: null,
      emptyPanoramicCheckPhoto: "",
      emptyPanoramicCheckB64Photo: "",
      emptyPanoramicValidationPhoto: "",
      emptyPanoramicCheckStatus: null,
      emptyPanoramicCheckComment: null,
      emptyStampNavieraCheckPhoto: "",
      emptyStampNavieraCheckB64Photo: "",
      emptyStampNavieraValidationPhoto: "",
      emptyStampNavieraCheckComment: null,
      emptyStampNavieraCheckStatus: null,
      emptyOtherStampCheckPhoto: "",
      emptyOtherStampCheckB64Photo: "",
      emptyOtherStampValidationPhoto: "",
      emptyOtherStampCheckComment: null,
      emptyOtherStampCheckStatus: null,
      emptySatelliteLockCheckPhoto: "",
      emptySatelliteLockCheckB64Photo: "",
      emptySatelliteLockValidationPhoto: "",
      emptySatelliteLockCheckComment: null,
      emptySatelliteLockCheckStatus: null,
      exitEngineryCheckPhoto1: "",
      exitEngineryCheckB64Photo1: "",
      exitEngineryValidationPhoto1: "",
      exitEngineryCheckComment1: null,
      exitEngineryCheckStatus1: null,
      exitEngineryCheckPhoto2: "",
      exitEngineryCheckB64Photo2: "",
      exitEngineryValidationPhoto2: "",
      exitEngineryCheckComment2: null,
      exitEngineryCheckStatus2: null,
      exitDoorCheckVideo: "",
      exitDoorCheckB64Video: "",
      exitDoorCheckComment: "",
      exitDoorCheckStatus: null,
      exitDoorValidationVideo: "",
      exitEngineryCheckVideo: "",
      exitEngineryCheckB64Video: "",
      exitEngineryCheckComment: "",
      exitEngineryCheckStatus: null,
      exitEngineryValidationVideo: "",
      chargingProcessPhoto1: "",
      chargingProcessComment1: "",
      chargingProcessPhoto2: "",
      chargingProcessComment2: "",
      chargingProcessVideo: "",
      containerPanoramicPhoto: "",
      containerPanoramicComment: "",
      containerPanoramicCoordinates: "",
      navieraBottlePhoto: "",
      navieraBottleComment: "",
      navieraWirePhoto: "",
      navieraWireComment: "",
      navieraLabelPhoto: "",
      navieraLabelComment: "",
      exporterBottlePhoto: "",
      exporterBottleComment: "",
      exporterWirePhoto: "",
      exporterWireComment: "",
      exporterLabelPhoto: "",
      exporterLabelComment: "",
      otherBottlePhoto: "",
      otherBottleComment: "",
      otherWirePhoto: "",
      otherWireComment: "",
      otherLabelPhoto: "",
      otherLabelComment: "",
      gpsPhoto: "",
      gpsComment: "",
      gpsStampPhoto: "",
      gpsStampComment: "",
      entryPort: "",
      exitDoorVideo: "",
      exitEngineryVideo: "",
    }));
  },
  step: "Formulario",
  setStep: (step) => set(() => ({ step })),
  resetSteps: () =>
    set(() => ({
      step: "Formulario",
    })),
});

export const useWorkflowTwoStore = create<WorkflowTwoI & WorkflowTwoActionI>()(
  devtools(
    persist(
      (...a) => ({
        ...createWorkflowSlice(...a),
      }),
      {
        name: "workflow-two-ecupro",
        storage: createJSONStorage(() => storage),
      }
    )
  )
);